/**
 * ProposalCard Component
 * Tier-aware pricing and proposal details
 * SMB: single plan, Midmarket: options, Enterprise: workstreams
 */

import React, { useMemo } from 'react';

const styles = {
  container: {
    padding: '60px 20px',
    marginBottom: '40px',
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '16px',
    color: '#BBBEC7',
    textAlign: 'center',
    marginBottom: '60px',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  card: {
    padding: '40px',
    borderRadius: '8px',
    border: '2px solid #2A3444',
    backgroundColor: '#1A2332',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
  },
  cardFeatured: {
    borderColor: '#0057FF',
    backgroundColor: 'rgba(0, 87, 255, 0.05)',
    transform: 'scale(1.05)',
  },
  badge: {
    display: 'inline-block',
    padding: '6px 12px',
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '16px',
    width: 'fit-content',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  planName: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
  },
  planDescription: {
    fontSize: '14px',
    color: '#BBBEC7',
    marginBottom: '24px',
    flex: 1,
    lineHeight: '1.6',
  },
  price: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#00D4AA',
    marginBottom: '8px',
  },
  pricePeriod: {
    fontSize: '14px',
    color: '#BBBEC7',
    marginBottom: '32px',
  },
  features: {
    marginBottom: '32px',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featureItem: {
    fontSize: '14px',
    color: '#BBBEC7',
    paddingLeft: '24px',
    marginBottom: '12px',
    position: 'relative',
    lineHeight: '1.5',
  },
  featureItemCheck: {
    position: 'absolute',
    left: 0,
    color: '#00D4AA',
    fontWeight: 'bold',
  },
  cta: {
    padding: '12px 24px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'center',
  },
  ctaPrimary: {
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
  },
  ctaPrimaryHover: {
    backgroundColor: '#003D99',
    boxShadow: '0 8px 16px rgba(0, 87, 255, 0.3)',
  },
  ctaSecondary: {
    backgroundColor: 'transparent',
    color: '#0057FF',
    border: '1px solid #0057FF',
  },
  ctaSecondaryHover: {
    backgroundColor: 'rgba(0, 87, 255, 0.1)',
  },
  workstreamContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '32px',
  },
  workstream: {
    padding: '20px',
    backgroundColor: '#0F1729',
    borderRadius: '6px',
    border: '1px solid #2A3444',
  },
  workstreamTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#00D4AA',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  workstreamText: {
    fontSize: '13px',
    color: '#BBBEC7',
    lineHeight: '1.5',
  },
};

/**
 * PricingCard component for individual plans
 */
function PricingCard({ plan, isFeatured = false, onSelect = null }) {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div
      style={{
        ...styles.card,
        ...(isFeatured && styles.cardFeatured),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {plan.badge && <div style={styles.badge}>{plan.badge}</div>}

      <h3 style={styles.planName}>{plan.name}</h3>
      <p style={styles.planDescription}>{plan.description}</p>

      <div style={styles.price}>{plan.price}</div>
      <div style={styles.pricePeriod}>{plan.period}</div>

      <div style={styles.features}>
        <ul style={styles.featureList}>
          {plan.features?.map((feature, idx) => (
            <li key={idx} style={styles.featureItem}>
              <span style={styles.featureItemCheck}>✓</span>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      <button
        style={{
          ...styles.cta,
          ...styles.ctaPrimary,
          ...(isHovered && styles.ctaPrimaryHover),
        }}
        onClick={() => onSelect && onSelect(plan)}
      >
        {plan.ctaText || 'Choose Plan'}
      </button>
    </div>
  );
}

/**
 * EnterpriseWorkstreams component
 */
function EnterpriseWorkstreams({ proposal }) {
  if (!proposal?.workstreams) return null;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Your Implementation Workstreams</h2>
      <p style={styles.subtitle}>
        Your proposal includes the following implementation areas:
      </p>

      <div style={styles.workstreamContainer}>
        {proposal.workstreams.map((ws, idx) => (
          <div key={idx} style={styles.workstream}>
            <div style={styles.workstreamTitle}>{ws.title}</div>
            <div style={styles.workstreamText}>{ws.description}</div>
            {ws.details && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#888' }}>
                {ws.details}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', color: '#BBBEC7', fontSize: '14px' }}>
        <strong>Total Investment:</strong> {proposal.totalPrice}
      </div>
    </div>
  );
}

/**
 * ProposalCard Component
 * @param {Object} props
 * @param {Object} props.prospect - Prospect data
 */
function ProposalCard({ prospect }) {
  const proposal = useMemo(() => {
    return (
      prospect?.proposal || {
        tier: prospect?.tier || 'smb',
        plans: [],
        workstreams: [],
      }
    );
  }, [prospect?.proposal, prospect?.tier]);

  // SMB: single plan
  if (proposal.tier === 'smb') {
    const plan = proposal.plans?.[0] || {
      name: 'Professional Plan',
      description: 'Complete solution for your business',
      price: '$2,999',
      period: 'per month',
      features: [
        'Core platform access',
        'Priority support',
        'Custom integration',
        'Monthly reporting',
      ],
    };

    return (
      <div style={styles.container}>
        <h2 style={styles.title}>Your Proposal</h2>
        <p style={styles.subtitle}>Designed specifically for your business needs</p>

        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <PricingCard plan={plan} isFeatured={true} />
        </div>
      </div>
    );
  }

  // Midmarket: multiple options
  if (proposal.tier === 'midmarket') {
    const plans = proposal.plans || [
      {
        name: 'Starter',
        description: 'Essential features',
        price: '$3,999',
        period: 'per month',
        features: [
          'Platform access',
          'Email support',
          'Up to 3 integrations',
          'Monthly reports',
        ],
      },
      {
        name: 'Professional',
        description: 'Most popular',
        badge: 'RECOMMENDED',
        price: '$5,999',
        period: 'per month',
        features: [
          'Full platform access',
          'Priority support',
          'Unlimited integrations',
          'Weekly reports',
          'Custom workflows',
        ],
      },
      {
        name: 'Enterprise',
        description: 'Advanced features',
        price: '$9,999',
        period: 'per month',
        features: [
          'Everything in Professional',
          '24/7 dedicated support',
          'Custom development',
          'Dedicated account manager',
          'Advanced analytics',
        ],
      },
    ];

    return (
      <div style={styles.container}>
        <h2 style={styles.title}>Choose Your Plan</h2>
        <p style={styles.subtitle}>Select the option that best fits your needs</p>

        <div style={styles.cardsGrid}>
          {plans.map((plan, idx) => (
            <PricingCard
              key={idx}
              plan={plan}
              isFeatured={plan.badge === 'RECOMMENDED'}
            />
          ))}
        </div>
      </div>
    );
  }

  // Enterprise: workstreams
  return <EnterpriseWorkstreams proposal={proposal} />;
}

ProposalCard.propTypes = {
  prospect: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'object') {
      return new Error(`${propName} must be an object`);
    }
  },
};

export default ProposalCard;
